/**
 * Resolve a human-friendly clickable label for a citation URL.
 *
 * Perplexity returns two parallel arrays in its API response:
 *   - `citations`: string[]                (URLs only — historical shape)
 *   - `search_results`: { title, url }[]   (full metadata — newer shape)
 *
 * The UI used to render only the URL hostname (e.g. "www.youtube.com"),
 * which is unhelpful when several citations share a domain. This helper
 * looks up the matching `search_results` entry and returns the page title
 * when available, falling back to the hostname so old cached payloads
 * still render correctly.
 */
export interface SearchResultMeta {
  title?: string;
  url: string;
  date?: string | null;
}

export function getCitationLabel(
  url: string,
  searchResults?: SearchResultMeta[] | null,
): string {
  if (searchResults && searchResults.length > 0) {
    const match = searchResults.find((r) => r?.url === url);
    const title = match?.title?.trim();
    if (title) return title;
  }
  return getUrlFallbackLabel(url);
}

/**
 * Build a readable fallback label from a URL when no title is available.
 * Returns "host · slug" where slug is the most meaningful path segment
 * (last non-numeric segment, de-slugified). Examples:
 *   https://www.lemonde.fr/economie/article/2024/05/12/quelque-chose.html
 *     → "lemonde.fr · Quelque chose"
 *   https://github.com/user/repo
 *     → "github.com · repo"
 *   https://example.com/
 *     → "example.com"
 */
export function getUrlFallbackLabel(url: string): string {
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, "");
    const segments = u.pathname.split("/").filter(Boolean);

    // Pick the last non-numeric, non-extension-only segment
    const meaningful = [...segments].reverse().find((s) => {
      const cleaned = s.replace(/\.[a-z0-9]{2,5}$/i, "");
      return cleaned.length > 2 && !/^\d+$/.test(cleaned);
    });

    if (!meaningful) return host;

    const slug = meaningful
      .replace(/\.[a-z0-9]{2,5}$/i, "")
      .replace(/[-_]+/g, " ")
      .replace(/\s+/g, " ")
      .trim();

    if (!slug) return host;

    const pretty = slug.charAt(0).toUpperCase() + slug.slice(1);
    const truncated = pretty.length > 70 ? pretty.slice(0, 67) + "…" : pretty;
    return `${host} · ${truncated}`;
  } catch {
    return url.replace(/^https?:\/\//, "").split("/")[0] || url;
  }
}

/**
 * Normalize a URL for deduplication: strips protocol, trailing slash,
 * "www." prefix, hash fragment and common tracking params (utm_*, fbclid,
 * gclid, ref, ref_src). Falls back to the raw string when URL parsing fails.
 */
export function normalizeUrlForDedup(url: string): string {
  try {
    const u = new URL(url);
    u.hash = "";
    const drop = /^(utm_|fbclid$|gclid$|ref$|ref_src$|mc_eid$|mc_cid$)/i;
    [...u.searchParams.keys()].forEach((k) => {
      if (drop.test(k)) u.searchParams.delete(k);
    });
    const host = u.hostname.replace(/^www\./, "");
    const path = u.pathname.replace(/\/+$/, "");
    const search = u.searchParams.toString();
    return `${host}${path}${search ? `?${search}` : ""}`.toLowerCase();
  } catch {
    return url.trim().toLowerCase();
  }
}

/** Deduplicate a list of URLs, returning each unique URL with its occurrence count. */
export function dedupeSources(urls: string[]): { url: string; count: number }[] {
  const map = new Map<string, { url: string; count: number }>();
  for (const url of urls) {
    if (!url) continue;
    const key = normalizeUrlForDedup(url);
    const existing = map.get(key);
    if (existing) existing.count += 1;
    else map.set(key, { url, count: 1 });
  }
  return [...map.values()];
}
